import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle, CheckCircle, Clock, Image as ImageIcon } from 'lucide-react';
import PageSeo from '../components/PageSeo';

const EvidenceViewer = () => {
  const { referenceNumber } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [evidence, setEvidence] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid access link - missing authentication token');
      setLoading(false);
      return;
    }

    fetchEvidence();
  }, [referenceNumber, token]);

  const fetchEvidence = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(
        `${backendUrl}/api/evidence/${referenceNumber}?token=${token}`
      );

      const result = await response.json();

      if (result.success) {
        setEvidence(result.evidence);
      } else {
        setError(result.error || 'Failed to load evidence');
      }
    } catch (err) {
      console.error('Error fetching evidence:', err);
      setError('Network error - please check your connection');
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadgeColor = (riskLevel) => {
    switch (riskLevel) {
      case 'HIGH':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'LOW':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getRiskIcon = (riskLevel) => {
    switch (riskLevel) {
      case 'HIGH':
        return <AlertTriangle className="w-6 h-6 text-red-600" />;
      case 'MEDIUM':
        return <Shield className="w-6 h-6 text-yellow-600" />;
      case 'LOW':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      default:
        return <Shield className="w-6 h-6 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <PageSeo
          title="Evidence Viewer | CashifyGCMart"
          description="Fraud detection evidence viewer."
          path=""
          noindex={true}
        />
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading evidence...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <PageSeo
          title="Evidence Viewer | CashifyGCMart"
          description="Fraud detection evidence viewer."
          path=""
          noindex={true}
        />
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-pink-600 text-white px-6 py-2 rounded-lg hover:bg-pink-700 transition"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (!evidence) {
    return null;
  }

  const fraudData = evidence.fraud_detection || {};
  const riskLevel = fraudData.risk_level || 'UNKNOWN';

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <PageSeo
        title="Evidence Viewer | CashifyGCMart"
        description="Fraud detection evidence viewer."
        path=""
        noindex={true}
      />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Fraud Detection Evidence</h1>
              <p className="text-gray-600 mt-1">Reference: {referenceNumber}</p>
            </div>
            {getRiskIcon(riskLevel)}
          </div>

          {/* Risk Badge */}
          <div className="flex items-center gap-4">
            <span className={`px-4 py-2 rounded-full border-2 font-semibold ${getRiskBadgeColor(riskLevel)}`}>
              {fraudData.risk_color} {riskLevel} RISK
            </span>
            <span className="text-gray-600">
              Score: {fraudData.fraud_score || 0}/100
            </span>
          </div>
        </div>

        {/* Submission Details */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Submission Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Submitted</p>
              <p className="font-semibold text-gray-900">
                {evidence.submitted_at ? new Date(evidence.submitted_at).toLocaleString() : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Card Brand</p>
              <p className="font-semibold text-gray-900">{evidence.card_info?.brand || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Card Value</p>
              <p className="font-semibold text-gray-900">${evidence.card_info?.value || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Fraud Flags */}
        {fraudData.flags && fraudData.flags.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Fraud Flags Detected
            </h2>
            <ul className="space-y-2">
              {fraudData.flags.map((flag, index) => (
                <li key={index} className="flex items-start gap-2 text-gray-700">
                  <span className="text-red-500 font-bold">•</span>
                  <span>{flag}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* AI Verification Results */}
        {fraudData.ai_verification && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">AI Verification Analysis</h2>
            <div className="space-y-3">
              {fraudData.ai_verification.card_appears_valid !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Card Appears Valid</span>
                  <span className={`font-semibold ${fraudData.ai_verification.card_appears_valid ? 'text-green-600' : 'text-red-600'}`}>
                    {fraudData.ai_verification.card_appears_valid ? 'Yes' : 'No'}
                  </span>
                </div>
              )}
              {fraudData.ai_verification.concern_level && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Concern Level</span>
                  <span className="font-semibold text-gray-900">{fraudData.ai_verification.concern_level}</span>
                </div>
              )}
              {fraudData.ai_verification.concerns && fraudData.ai_verification.concerns.length > 0 && (
                <div>
                  <p className="text-gray-700 font-semibold mb-2">AI Concerns:</p>
                  <ul className="space-y-1 pl-4">
                    {fraudData.ai_verification.concerns.map((concern, index) => (
                      <li key={index} className="text-gray-600">• {concern}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Duplicate Check Results */}
        {fraudData.duplicate_check && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Duplicate Detection</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Duplicate Found</span>
                <span className={`font-semibold ${fraudData.duplicate_check.is_duplicate ? 'text-red-600' : 'text-green-600'}`}>
                  {fraudData.duplicate_check.is_duplicate ? 'Yes' : 'No'}
                </span>
              </div>
              {fraudData.duplicate_check.match_count > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Similar Images Found</span>
                  <span className="font-semibold text-red-600">{fraudData.duplicate_check.match_count}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Card Images */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Card Images
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {evidence.images?.front && (
              <div>
                <p className="text-sm text-gray-600 mb-2 font-semibold">Front Image</p>
                <img
                  src={evidence.images.front.data || evidence.images.front}
                  alt="Card Front"
                  loading="lazy"
                  className="w-full rounded-lg border-2 border-gray-200"
                />
              </div>
            )}
            {evidence.images?.back && (
              <div>
                <p className="text-sm text-gray-600 mb-2 font-semibold">Back Image</p>
                <img
                  src={evidence.images.back.data || evidence.images.back}
                  alt="Card Back"
                  loading="lazy"
                  className="w-full rounded-lg border-2 border-gray-200"
                />
              </div>
            )}
          </div>
        </div>

        {/* Detection Metadata */}
        <div className="bg-gray-100 rounded-lg p-4 mt-6 text-center text-sm text-gray-600">
          <Clock className="w-4 h-4 inline mr-2" />
          Detection completed in {fraudData.detection_time || 0}ms
        </div>
      </div>
    </div>
  );
};

export default EvidenceViewer;
